// Traffic Service
// Provides real-time traffic data using Google Maps API
import { API_BASE_URL } from '../config/environment.js';
import { authFetch } from '../utils/authFetch';

/**
 * Get real-time travel time from origin to destination
 * @param {Object} origin - Origin address object
 * @param {Object} destination - Destination address object
 * @param {string} departureTime - ISO string of departure time (optional, for future traffic prediction)
 * @returns {Promise<Object>} Travel time and route information
 */
export const getRealTimeTravelTime = async (origin, destination, departureTime = null) => {
  try {
    // Validate departure time - don't request traffic for past times
    const now = new Date();
    const departureDate = departureTime ? new Date(departureTime) : now;
    
    // If departure time is in the past, use current time instead
    const validDepartureTime = departureDate < now ? now : departureDate;

    // Format addresses for Google Maps API
    const originAddress = formatAddressForMaps(origin);
    const destinationAddress = formatAddressForMaps(destination);

    // Use API Gateway proxy instead of direct Google Maps call (avoids CORS issues)
    const baseUrl = `${API_BASE_URL}/google-maps`;
    const params = new URLSearchParams({
      origins: originAddress,
      destinations: destinationAddress,
      units: 'imperial',
      mode: 'driving',
      traffic_model: 'best_guess',
      departure_time: Math.floor(validDepartureTime.getTime() / 1000)
    });

    const url = `${baseUrl}?${params}`;

    // Make API request
    const response = await authFetch(url);
    if (!response.ok) {
      throw new Error(`Google Maps API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Check for API errors
    if (data.status !== 'OK') {
      throw new Error(`Google Maps API error: ${data.status} - ${data.error_message || 'Unknown error'}`);
    }

    // Extract travel time information
    const element = data.rows[0]?.elements[0];
    if (!element || element.status !== 'OK') {
      throw new Error(`No route found: ${element?.status || 'Unknown error'}`);
    }

    // Get duration in traffic (if available) or regular duration
    const durationInTraffic = element.duration_in_traffic;
    const duration = element.duration;
    const distance = element.distance;

    const result = {
      travelTimeMinutes: Math.ceil((durationInTraffic?.value || duration.value) / 60),
      distanceMiles: Math.round(distance.value * 0.000621371 * 100) / 100, // Convert meters to miles
      hasTrafficData: !!durationInTraffic,
      trafficDelayMinutes: durationInTraffic ? Math.ceil((durationInTraffic.value - duration.value) / 60) : 0,
      route: {
        origin: data.origin_addresses[0],
        destination: data.destination_addresses[0]
      },
      timestamp: new Date().toISOString()
    };

    return result;

  } catch (error) {
    // Silent fallback — Google Maps API may not be configured
    return getFallbackTravelTime(origin, destination);
  }
};

/**
 * Get travel time from home address to airport using real-time traffic
 * @param {Object} homeAddress - User's home address
 * @param {string} airportCode - Airport code (e.g., 'DFW', 'SEA')
 * @param {string} departureTime - ISO string of departure time (optional)
 * @returns {Promise<Object>} Travel time information
 */
export const getAirportTravelTime = async (homeAddress, airportCode, departureTime = null) => {
  try {
    if (!airportCode) {
      throw new Error('Airport code is required');
    }

    // If no home address (return flight), use fallback estimates immediately
    if (!homeAddress || !homeAddress.city) {
      return {
        travelTimeMinutes: 45, // Default estimate for return flights
        distanceMiles: null,
        hasTrafficData: false,
        trafficDelayMinutes: 0,
        route: null,
        timestamp: null,
        fallback: true,
        airportCode,
        airportName: getAirportName(airportCode)
      };
    }

    // Normalize airport code - strip ICAO "K" prefix for US airports
    const normalizedCode = airportCode.replace(/^K([A-Z]{3})$/, '$1');

    // Get airport address from airport code
    const airportAddress = getAirportAddress(normalizedCode);
    if (!airportAddress) {
      throw new Error(`Unknown airport code: ${airportCode} (normalized: ${normalizedCode})`);
    }

    // Get real-time travel time
    const travelData = await getRealTimeTravelTime(homeAddress, airportAddress, departureTime);

    return {
      ...travelData,
      airportCode: normalizedCode,
      airportName: getAirportName(normalizedCode)
    };

  } catch (error) {
    console.error('Error getting airport travel time:', error);
    // Fall back to estimated time
    return {
      travelTimeMinutes: getEstimatedTravelTime(homeAddress?.city, airportCode),
      distanceMiles: null,
      hasTrafficData: false,
      trafficDelayMinutes: 0,
      airportCode,
      airportName: getAirportName(airportCode),
      fallback: true,
      error: error.message
    };
  }
};

/**
 * Format address object for Google Maps API
 * @param {Object} address - Address object
 * @returns {string} Formatted address string
 */
const formatAddressForMaps = (address) => {
  if (typeof address === 'string') {
    return address;
  }

  const parts = [];
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.zipCode) parts.push(address.zipCode);
  if (address.country && address.country !== 'US') parts.push(address.country);

  return parts.join(', ');
};

/**
 * Get airport address from airport code
 * @param {string} airportCode - Airport code
 * @returns {Object|null} Airport address object
 */
const getAirportAddress = (airportCode) => {
  const airportAddresses = {
    'DFW': {
      street: '2400 Aviation Dr',
      city: 'Dallas',
      state: 'TX',
      zipCode: '75261',
      country: 'US'
    },
    'SEA': {
      street: '17801 International Blvd',
      city: 'Seattle',
      state: 'WA',
      zipCode: '98158',
      country: 'US'
    },
    'JFK': {
      street: 'Queens',
      city: 'New York',
      state: 'NY',
      zipCode: '11430',
      country: 'US'
    },
    'LAX': {
      street: '1 World Way',
      city: 'Los Angeles',
      state: 'CA',
      zipCode: '90045',
      country: 'US'
    },
    'ORD': {
      street: '10000 W O\'Hare Ave',
      city: 'Chicago',
      state: 'IL',
      zipCode: '60666',
      country: 'US'
    },
    'ATL': {
      street: '6000 N Terminal Pkwy',
      city: 'Atlanta',
      state: 'GA',
      zipCode: '30320',
      country: 'US'
    },
    'DEN': {
      street: '8500 Pena Blvd',
      city: 'Denver',
      state: 'CO',
      zipCode: '80249',
      country: 'US'
    },
    'SFO': {
      street: 'San Francisco International Airport',
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94128',
      country: 'US'
    },
    'YYZ': {
      street: '6301 Silver Dart Dr',
      city: 'Mississauga',
      state: 'ON',
      zipCode: 'L5P 1B2',
      country: 'CA'
    },
    'DAL': {
      street: '8008 Herb Kelleher Way',
      city: 'Dallas',
      state: 'TX',
      zipCode: '75235',
      country: 'US'
    },
    'AUS': {
      street: '3600 Presidential Blvd',
      city: 'Austin',
      state: 'TX',
      zipCode: '78719',
      country: 'US'
    }
  };

  return airportAddresses[airportCode] || null;
};

/**
 * Get airport name from airport code
 * @param {string} airportCode - Airport code
 * @returns {string} Airport name
 */
const getAirportName = (airportCode) => {
  const airportNames = {
    'DFW': 'Dallas/Fort Worth International Airport',
    'SEA': 'Seattle-Tacoma International Airport',
    'JFK': 'John F. Kennedy International Airport',
    'LAX': 'Los Angeles International Airport',
    'ORD': 'O\'Hare International Airport',
    'ATL': 'Hartsfield-Jackson Atlanta International Airport',
    'DEN': 'Denver International Airport',
    'SFO': 'San Francisco International Airport',
    'YYZ': 'Toronto Pearson International Airport',
    'DAL': 'Dallas Love Field',
    'AUS': 'Austin-Bergstrom International Airport'
  };

  return airportNames[airportCode] || `${airportCode} Airport`;
};

/**
 * Fallback travel time calculation when Google Maps API is not available
 * @param {Object} origin - Origin address
 * @param {Object} destination - Destination address
 * @returns {Object} Fallback travel time data
 */
const getFallbackTravelTime = (origin, destination) => {
  // Extract airport code from destination if it's an airport
  let airportCode = null;
  if (destination.airportCode) {
    airportCode = destination.airportCode;
  } else if (destination.street && destination.street.includes('Airport')) {
    // Try to extract airport code from address
    const airportNames = Object.keys(getAirportAddress);
    for (const code of airportNames) {
      if (destination.street.includes(getAirportName(code).split(' ')[0])) {
        airportCode = code;
        break;
      }
    }
  }

  const estimatedTime = airportCode ? 
    getEstimatedTravelTime(origin.city, airportCode) : 
    45; // Default 45 minutes

  return {
    travelTimeMinutes: estimatedTime,
    distanceMiles: null,
    hasTrafficData: false,
    trafficDelayMinutes: 0,
    route: {
      origin: formatAddressForMaps(origin),
      destination: formatAddressForMaps(destination)
    },
    timestamp: new Date().toISOString(),
    fallback: true
  };
};

/**
 * Get estimated travel time (fallback method)
 * @param {string} city - User's city
 * @param {string} airportCode - Airport code
 * @returns {number} Estimated travel time in minutes
 */
const getEstimatedTravelTime = (city, airportCode) => {
  // Use the same logic as the original departure calculator
  const travelEstimates = {
    'Dallas': { 'DFW': 45, 'DAL': 20 },
    'Fort Worth': { 'DFW': 30, 'DAL': 50 },
    'Plano': { 'DFW': 35, 'DAL': 40 },
    'Irving': { 'DFW': 25, 'DAL': 35 },
    'Seattle': { 'SEA': 30, 'BFI': 15 },
    'Bellevue': { 'SEA': 25, 'BFI': 20 },
    'Redmond': { 'SEA': 35, 'BFI': 30 },
    'New York': { 'JFK': 60, 'LGA': 45, 'EWR': 50 },
    'Manhattan': { 'JFK': 60, 'LGA': 45, 'EWR': 50 },
    'Brooklyn': { 'JFK': 45, 'LGA': 35, 'EWR': 60 },
    'Los Angeles': { 'LAX': 45, 'BUR': 30, 'LGB': 40 },
    'Beverly Hills': { 'LAX': 35, 'BUR': 25, 'LGB': 45 },
    'Santa Monica': { 'LAX': 25, 'BUR': 35, 'LGB': 40 }
  };

  const normalizedCity = city.toLowerCase()
    .replace(/\s+(city|town|village)$/, '')
    .replace(/\s+heights?$/, '')
    .replace(/\s+park$/, '');

  if (travelEstimates[normalizedCity] && travelEstimates[normalizedCity][airportCode]) {
    return travelEstimates[normalizedCity][airportCode];
  }

  for (const [cityKey, airports] of Object.entries(travelEstimates)) {
    if (normalizedCity.includes(cityKey) || cityKey.includes(normalizedCity)) {
      if (airports[airportCode]) {
        return airports[airportCode];
      }
    }
  }

  const defaultEstimates = {
    'DFW': 45, 'SEA': 35, 'JFK': 60, 'LAX': 45, 'ORD': 50,
    'ATL': 40, 'DEN': 35, 'SFO': 40, 'YYZ': 50, 'DAL': 20, 'AUS': 35
  };

  return defaultEstimates[airportCode] || 45;
};

